import type { InviteRequestValues, NewUserRecord } from '../types';

/**
 * Flat form error map keyed by field name.
 */
export type FormErrors<T extends object> = Partial<Record<keyof T | 'form', string>>;

/**
 * Login form values.
 */
export interface LoginValues {
  /** Email address. */
  email: string;
  /** Password. */
  password: string;
}

/**
 * Signup form values.
 */
export interface SignupValues extends NewUserRecord {
  /** Password. */
  password: string;
}

/**
 * Password change form values.
 */
export interface PasswordChangeValues {
  /** Current password. */
  oldPass: string;
  /** New password. */
  newPass: string;
  /** Repeated new password. */
  confirmPass: string;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true when a string looks like an email address.
 */
export function isValidEmail(value: string): boolean {
  return emailPattern.test(value.trim());
}

/**
 * Validates login form values.
 */
export function validateLogin(values: LoginValues): FormErrors<LoginValues> {
  const errors: FormErrors<LoginValues> = {};
  if (!isValidEmail(values.email)) {
    errors.email = 'Invalid email';
  }
  if (values.password.length === 0) {
    errors.password = 'Required';
  }
  return errors;
}

/**
 * Validates password reset form values.
 */
export function validateReset(email: string): { email?: string } {
  return isValidEmail(email) ? {} : { email: 'Invalid email' };
}

/**
 * Validates signup form values.
 */
export function validateSignup(values: SignupValues): FormErrors<SignupValues> {
  const errors: FormErrors<SignupValues> = {};
  if (values.firstname.trim().length === 0) {
    errors.firstname = 'Required';
  } else if (values.firstname.length > 35) {
    errors.firstname = 'Must be 35 characters or less';
  }

  if (values.lastname.trim().length === 0) {
    errors.lastname = 'Required';
  } else if (values.lastname.length > 35) {
    errors.lastname = 'Must be 35 characters or less';
  }

  if (!isValidEmail(values.email)) {
    errors.email = 'Invalid email address.';
  }

  if (values.password.length < 6) {
    errors.password = 'Must be at least 6 characters long.';
  }

  return errors;
}

/**
 * Validates beta invitation request values.
 */
export function validateInviteRequest(values: InviteRequestValues): FormErrors<InviteRequestValues> {
  const errors: FormErrors<InviteRequestValues> = {};
  if (values.name.trim().length === 0) {
    errors.name = 'Required';
  } else if (values.name.length > 70) {
    errors.name = 'Must be 70 characters or less';
  }
  if (!isValidEmail(values.email)) {
    errors.email = 'Invalid email address.';
  }
  if (values.about.trim().length === 0) {
    errors.about = 'Required';
  }
  return errors;
}

/**
 * Validates password change values.
 */
export function validatePasswordChange(values: PasswordChangeValues): FormErrors<PasswordChangeValues> {
  const errors: FormErrors<PasswordChangeValues> = {};
  if (values.oldPass.length === 0) {
    errors.oldPass = 'Required';
  }
  if (values.newPass.length < 6) {
    errors.newPass = 'Must be at least 6 characters long.';
  }
  if (values.newPass !== values.confirmPass) {
    errors.confirmPass = 'New passwords must match.';
  }
  return errors;
}

/**
 * Returns true when a form error map has no errors.
 */
export function hasNoErrors(errors: object): boolean {
  return Object.keys(errors).length === 0;
}
